import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Clock, Calendar, ExternalLink } from 'lucide-react';
import { defaultEventDayContent } from '../../data/pageContentDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map an icon name from the CMS to a lucide component. Kept small on
// purpose — Wedding Day Details currently only uses two shapes.
const ICONS = { Calendar, Clock };

const EventDetails = () => {
  const [c, setC] = useState(defaultEventDayContent);

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/event-day/content`, { params: { _: Date.now() } });
        if (!alive || !res.data) return;
        setC({ ...defaultEventDayContent, ...res.data });
      } catch { /* keep defaults */ }
    };
    fetchContent();
    const interval = setInterval(fetchContent, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchContent(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const venue = c.venue || {};
  const dressCode = c.dressCode || {};
  const cards = c.cards && c.cards.length ? c.cards : defaultEventDayContent.cards;
  const bg = c.backgroundImageUrl || defaultEventDayContent.backgroundImageUrl;

  return (
    <section id="events" className="relative min-h-screen flex items-center" data-testid="events-section">
      <div className="absolute inset-0 z-0">
        <img src={bg} alt="Wedding venue" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#3B2F2F]/60" />
      </div>

      <div className="relative z-10 w-full py-20 md:py-32">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="font-cursive text-[#C8A951] text-2xl mb-2">{c.eyebrow}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
              {c.title}
            </h2>
            <div className="gold-divider" />
          </motion.div>

          {/* Venue Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-[#C8A951]" />
              <span className="text-[#C8A951] text-sm uppercase tracking-widest">{c.venueLabel}</span>
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-white mb-2" data-testid="event-details-venue-name">{venue.name}</h3>
            <p className="text-white/80 mb-4" data-testid="event-details-venue-address">{venue.address}</p>
            {venue.mapUrl && (
              <a
                href={venue.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full text-white hover:bg-white/20 transition-colors"
                data-testid="venue-map-link"
              >
                <span className="text-sm uppercase tracking-wider">{venue.mapButtonLabel || 'View on Google Maps'}</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </motion.div>

          {/* Cards Grid — driven by shared schedule records */}
          <div className="grid md:grid-cols-3 gap-6">
            {cards.map((card, index) => {
              const Icon = ICONS[card.icon] || Calendar;
              return (
                <motion.div
                  key={card.key || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20"
                  data-testid={`event-day-card-${card.key || index}`}
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C8A951]/20 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-[#C8A951]" />
                  </div>
                  <h3 className="font-serif text-xl text-white mb-2">{card.title}</h3>
                  <p className="text-[#C8A951] font-medium mb-3">{card.time}</p>
                  <p className="text-white/70 text-sm">{card.description}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Dress Code */}
          {(dressCode.label || dressCode.value || dressCode.note) && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-12 text-center"
            >
              {(dressCode.label || dressCode.value) && (
                <div className="inline-block bg-[#C8A951] text-white px-8 py-4 rounded-full">
                  <p className="text-sm uppercase tracking-widest">
                    {dressCode.label || 'Dress Code'}: <span className="font-semibold">{dressCode.value}</span>
                  </p>
                </div>
              )}
              {dressCode.note && <p className="text-white/70 text-sm mt-3">{dressCode.note}</p>}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export { EventDetails };
